import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Play, TrendingUp, Target } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const EnhancedVideoHeatmap = ({ 
  gameId, 
  stats, 
  duration = 300,
  onSeekTo,
  timecodes = [],
  compact = false
}) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [hoveredSecond, setHoveredSecond] = useState(null);

  // Calculate retention and activity data
  const calculateData = useCallback(() => {
    if (!stats?.video_stats?.total_views) return { retention: [], peaks: [] };
    
    const totalViews = stats.video_stats.total_views;
    const heatmapData = stats.timecode_stats?.heatmap_data || [];
    const dropOffPoints = stats.timecode_stats?.drop_off_points || [];
    
    // Create maps
    const dropOffMap = {};
    dropOffPoints.forEach(p => { dropOffMap[p.second] = p.count; });
    
    const activityMap = {};
    heatmapData.forEach(p => { activityMap[p.second] = p.count; });
    
    // Build retention curve
    const retention = [];
    let currentViewers = totalViews;
    
    for (let sec = 0; sec <= duration; sec++) {
      if (dropOffMap[sec]) {
        currentViewers -= dropOffMap[sec];
      }
      retention.push({
        second: sec,
        viewers: Math.max(0, currentViewers),
        percentage: Math.max(0, (currentViewers / totalViews) * 100),
        activity: activityMap[sec] || 0,
        dropOff: dropOffMap[sec] || 0
      });
    }
    
    // Find peak moments (most viewed/replayed sections)
    const peaks = [];
    const avgActivity = heatmapData.length > 0 
      ? heatmapData.reduce((a, b) => a + b.count, 0) / heatmapData.length 
      : 0;
    
    heatmapData.forEach(p => {
      if (p.count > avgActivity * 1.5) {
        peaks.push({ second: p.second, count: p.count, label: 'Популярный момент' });
      }
    });
    
    return { retention, peaks };
  }, [stats, duration]);

  const { retention, peaks } = calculateData();

  // Draw the YouTube-style heatmap
  useEffect(() => {
    if (!canvasRef.current || retention.length === 0) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    const chartHeight = height - 25;
    
    // Clear
    ctx.fillStyle = compact ? '#f8f9fa' : '#0f0f1a';
    ctx.fillRect(0, 0, width, height);
    
    const xScale = width / duration;
    const maxActivity = Math.max(...retention.map(r => r.activity), 1);
    
    // Draw YouTube-style most replayed bars
    // Gray base bars for all activity
    retention.forEach((point, i) => {
      if (point.activity > 0) {
        const x = i * xScale;
        const intensity = point.activity / maxActivity;
        const barHeight = 20 + intensity * (chartHeight - 40);
        
        // Base gray bar
        ctx.fillStyle = compact ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)';
        ctx.fillRect(x, chartHeight - barHeight, Math.max(xScale, 2), barHeight);
      }
    });
    
    // Highlight peaks (most replayed) with gradient
    const avgHeight = chartHeight * 0.4;
    retention.forEach((point, i) => {
      if (point.activity > 0) {
        const x = i * xScale;
        const intensity = point.activity / maxActivity;
        const barHeight = 20 + intensity * (chartHeight - 40);
        
        // Color based on popularity
        if (intensity > 0.5) {
          // Hot (most replayed) - white/bright
          const gradient = ctx.createLinearGradient(x, chartHeight - barHeight, x, chartHeight);
          gradient.addColorStop(0, compact ? 'rgba(59, 130, 246, 0.8)' : 'rgba(255, 255, 255, 0.9)');
          gradient.addColorStop(1, compact ? 'rgba(59, 130, 246, 0.2)' : 'rgba(255, 255, 255, 0.2)');
          ctx.fillStyle = gradient;
          ctx.fillRect(x, chartHeight - barHeight, Math.max(xScale, 2), barHeight);
        } else if (intensity > 0.25) {
          // Medium - semi-transparent
          ctx.fillStyle = compact ? 'rgba(59, 130, 246, 0.4)' : 'rgba(255, 255, 255, 0.4)';
          ctx.fillRect(x, chartHeight - barHeight, Math.max(xScale, 2), barHeight);
        }
      }
    });
    
    // Draw retention line on top
    ctx.beginPath();
    ctx.strokeStyle = '#22c55e';
    ctx.lineWidth = 2;
    retention.forEach((point, i) => {
      const x = i * xScale;
      const y = chartHeight - (point.percentage / 100) * (chartHeight - 10);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.stroke();
    
    // Draw timecode markers
    timecodes.forEach(tc => {
      const x = tc.timecode * xScale;
      
      // Vertical line
      ctx.strokeStyle = 'rgba(34, 197, 94, 0.6)';
      ctx.lineWidth = 2;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, chartHeight);
      ctx.stroke();
      ctx.setLineDash([]);
      
      // Circle marker
      ctx.fillStyle = '#22c55e';
      ctx.beginPath();
      ctx.arc(x, 8, 5, 0, Math.PI * 2);
      ctx.fill();
    });
    
    // Draw drop-off markers (red)
    retention.forEach((point, i) => {
      if (point.dropOff > 0) {
        const x = i * xScale;
        ctx.fillStyle = '#ef4444';
        ctx.beginPath();
        ctx.arc(x, chartHeight - 8, 4, 0, Math.PI * 2);
        ctx.fill();
      }
    });
    
    // Hover line
    if (hoveredSecond !== null) {
      const x = hoveredSecond * xScale;
      ctx.strokeStyle = compact ? '#212529' : '#fff';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, chartHeight);
      ctx.stroke();
    }
    
    // Timeline bar
    ctx.fillStyle = compact ? '#dee2e6' : '#374151';
    ctx.fillRect(0, chartHeight + 3, width, 3);
    
    // Time labels
    ctx.fillStyle = compact ? '#6c757d' : '#9ca3af';
    ctx.font = '10px sans-serif';
    ctx.textAlign = 'center';
    
    const labels = compact ? 3 : 5;
    for (let i = 0; i <= labels; i++) {
      const sec = Math.floor((duration / labels) * i);
      const x = (sec / duration) * width;
      ctx.fillText(formatTime(sec), x, chartHeight + 16);
    }
    
  }, [retention, timecodes, duration, hoveredSecond, compact]);

  // Mouse handlers
  const handleMouseMove = (e) => {
    if (!containerRef.current || retention.length === 0) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const second = Math.floor((x / rect.width) * duration);
    
    if (second >= 0 && second < retention.length) {
      setHoveredSecond(second);
      
      const point = retention[second];
      const tcAtSecond = timecodes.find(tc => Math.abs(tc.timecode - second) < 2);
      const isPeak = point.activity > (Math.max(...retention.map(r => r.activity)) * 0.5);
      
      setTooltip({
        x: Math.min(e.clientX - rect.left, rect.width - 180),
        y: Math.max(e.clientY - rect.top - 100, 10),
        second,
        viewers: point.viewers,
        percentage: point.percentage,
        activity: point.activity,
        dropOff: point.dropOff,
        timecode: tcAtSecond,
        isPeak
      });
    }
  };

  const handleMouseLeave = () => {
    setHoveredSecond(null);
    setTooltip(null);
  };

  const handleClick = (e) => {
    if (!containerRef.current || !onSeekTo) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const second = Math.floor((x / rect.width) * duration);
    onSeekTo(second);
  };

  if (!stats?.video_stats?.total_views) return null;

  // Find most replayed segment
  const maxActivity = Math.max(...retention.map(r => r.activity));
  const mostReplayed = retention.find(r => r.activity === maxActivity);

  return (
    <div style={{ 
      background: compact ? '#f8f9fa' : '#1a1a2e', 
      borderRadius: compact ? 10 : 12, 
      padding: compact ? 14 : 16,
      border: compact ? '1px solid #e9ecef' : 'none'
    }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: 12,
        flexWrap: 'wrap',
        gap: 8
      }}>
        <div style={{ 
          fontSize: compact ? 13 : 14, 
          fontWeight: 600, 
          color: compact ? '#212529' : '#fff' 
        }}>
          Наиболее просматриваемые моменты
        </div>
        
        {/* Legend */}
        <div style={{ display: 'flex', gap: 12, fontSize: 10, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ 
              width: 16, height: 10, 
              background: compact 
                ? 'linear-gradient(to bottom, rgba(59,130,246,0.8), rgba(59,130,246,0.2))' 
                : 'linear-gradient(to bottom, rgba(255,255,255,0.9), rgba(255,255,255,0.2))',
              borderRadius: 2 
            }} />
            <span style={{ color: compact ? '#6c757d' : '#9ca3af' }}>Популярное</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 12, height: 3, background: '#22c55e', borderRadius: 2 }} />
            <span style={{ color: compact ? '#6c757d' : '#9ca3af' }}>Удержание</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 8, height: 8, background: '#22c55e', borderRadius: '50%' }} />
            <span style={{ color: compact ? '#6c757d' : '#9ca3af' }}>Таймкод</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 8, height: 8, background: '#ef4444', borderRadius: '50%' }} />
            <span style={{ color: compact ? '#6c757d' : '#9ca3af' }}>Уход</span>
          </div>
        </div>
      </div>

      {/* Most replayed badge */}
      {mostReplayed && mostReplayed.activity > 0 && (
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '4px 10px',
          background: compact ? '#dbeafe' : '#1e3a5f',
          borderRadius: 6,
          marginBottom: 10,
          fontSize: 11
        }}>
          <TrendingUp size={12} color={compact ? '#3b82f6' : '#60a5fa'} />
          <span style={{ color: compact ? '#1e40af' : '#93c5fd' }}>
            Самый популярный момент: <strong>{formatTime(mostReplayed.second)}</strong>
          </span>
        </div>
      )}
      
      {/* Canvas */}
      <div 
        ref={containerRef}
        style={{ position: 'relative', cursor: onSeekTo ? 'pointer' : 'default' }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
      >
        <canvas 
          ref={canvasRef} 
          width={800} 
          height={compact ? 100 : 140} 
          style={{ width: '100%', height: compact ? 100 : 140, borderRadius: 8 }}
        />
        
        {/* Tooltip */}
        {tooltip && (
          <div style={{
            position: 'absolute',
            left: tooltip.x,
            top: tooltip.y,
            background: compact ? '#212529' : '#0f0f1a',
            border: `1px solid ${compact ? '#495057' : '#374151'}`,
            borderRadius: 8,
            padding: '10px 14px',
            zIndex: 100,
            minWidth: 160,
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            pointerEvents: 'none'
          }}>
            <div style={{ 
              fontSize: 13, 
              fontWeight: 600, 
              color: '#fff', 
              marginBottom: 6,
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}>
              {formatTime(tooltip.second)}
              {tooltip.isPeak && (
                <span style={{
                  fontSize: 9,
                  background: '#3b82f6',
                  color: '#fff',
                  padding: '2px 6px',
                  borderRadius: 4
                }}>
                  ПОПУЛЯРНОЕ
                </span>
              )}
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                <span style={{ color: '#9ca3af' }}>Зрителей:</span>
                <span style={{ color: '#22c55e', fontWeight: 500 }}>
                  {tooltip.percentage.toFixed(0)}%
                </span>
              </div>
              
              {tooltip.activity > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                  <span style={{ color: '#9ca3af' }}>Просмотров:</span>
                  <span style={{ color: '#3b82f6', fontWeight: 500 }}>{tooltip.activity}</span>
                </div>
              )}
              
              {tooltip.dropOff > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                  <span style={{ color: '#9ca3af' }}>Ушло:</span>
                  <span style={{ color: '#ef4444', fontWeight: 500 }}>{tooltip.dropOff}</span>
                </div>
              )}
              
              {tooltip.timecode && (
                <div style={{ 
                  marginTop: 4, 
                  paddingTop: 4, 
                  borderTop: '1px solid #374151',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4
                }}>
                  <Target size={10} color="#22c55e" />
                  <span style={{ fontSize: 10, color: '#22c55e' }}>Переход на панораму</span>
                </div>
              )}
            </div>
            
            {onSeekTo && (
              <div style={{ 
                marginTop: 6, 
                paddingTop: 4, 
                borderTop: '1px solid #374151',
                fontSize: 9, 
                color: '#6b7280',
                textAlign: 'center'
              }}>
                Нажмите для перехода
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Summary stats */}
      {!compact && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-around', 
          marginTop: 12,
          paddingTop: 12,
          borderTop: '1px solid #2d2d44'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#22c55e' }}>
              {retention.length > 0 ? retention[retention.length - 1]?.percentage.toFixed(0) : 0}%
            </div>
            <div style={{ fontSize: 10, color: '#6b7280' }}>Досмотрели</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#3b82f6' }}>
              {mostReplayed ? formatTime(mostReplayed.second) : '—'}
            </div>
            <div style={{ fontSize: 10, color: '#6b7280' }}>Пик интереса</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#22c55e' }}>
              {timecodes.length}
            </div>
            <div style={{ fontSize: 10, color: '#6b7280' }}>Таймкодов</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#ef4444' }}>
              {retention.filter(r => r.dropOff > 0).length}
            </div>
            <div style={{ fontSize: 10, color: '#6b7280' }}>Точек ухода</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedVideoHeatmap;
